(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-collection-device-navigation]';
  const COMPACT_ENTER_BUFFER = 1;
  const COMPACT_EXIT_BUFFER = 16;

  const normalize = (value) => String(value || '').trim().toLowerCase();

  const headerOffset = () => {
    const style = getComputedStyle(document.documentElement);
    const stored = parseFloat(style.getPropertyValue('--site-sticky-offset'));
    if (Number.isFinite(stored) && stored > 0) return stored;
    const fallback = parseFloat(style.getPropertyValue('--header-height'));
    return Number.isFinite(fallback) ? fallback : 0;
  };

  class CollectionDeviceNavigation {
    constructor(root) {
      if (!root || root.dataset.deviceNavigationReady === 'true') return;
      root.dataset.deviceNavigationReady = 'true';
      this.root = root;
      this.root.__collectionDeviceNavigation = this;
      this.kind = root.dataset.navigationKind || '';
      this.viewport = root.querySelector('[data-device-navigation-viewport]');
      this.track = root.querySelector('[data-device-navigation-track]');
      this.previous = root.querySelector('[data-device-navigation-previous]');
      this.next = root.querySelector('[data-device-navigation-next]');
      this.sentinel = root.querySelector('[data-collection-device-navigation-sentinel]');
      this.options = Array.from(root.querySelectorAll('[data-device-navigation-option]'));
      this.compact = root.classList.contains('is-compact');
      this.refreshFrame = 0;
      this.compactFrame = 0;
      this.arrowFrame = 0;

      this.onResize = () => this.scheduleRefresh();
      this.onScroll = () => this.scheduleArrowRefresh();
      this.onWindowScroll = () => this.scheduleCompactRefresh();
      this.onHistoryRestore = () => {
        this.clearStaleCollectionLoading();
        this.initializeSelection();
        window.requestAnimationFrame(() => this.clearStaleCollectionLoading());
      };

      this.clearStaleCollectionLoading();
      this.bind();
      this.initializeSelection();
      this.refresh();
    }

    bind() {
      this.previous?.addEventListener('click', () => this.scroll(-1));
      this.next?.addEventListener('click', () => this.scroll(1));
      this.viewport?.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('scroll', this.onWindowScroll, { passive: true });
      window.addEventListener('popstate', this.onHistoryRestore);
      window.addEventListener('pageshow', this.onHistoryRestore);

      if (this.kind === 'royal') {
        this.options.forEach((option) => {
          option.addEventListener('click', () => this.selectRoyalModel(option, true));
        });
      } else if (this.kind === 'apple') {
        this.options.forEach((option) => {
          option.addEventListener('click', () => {
            const size = option.dataset.appleSize || '';
            if (size) window.BrilintonDeviceContext?.rememberAppleWatch({ size }, 'collection-device-navigation');
          });
        });
      }

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleRefresh());
        if (this.viewport) this.resizeObserver.observe(this.viewport);
        if (this.track) this.resizeObserver.observe(this.track);
      }
    }


    clearStaleCollectionLoading() {
      document.querySelectorAll('#ProductGridContainer .collection.loading').forEach((collection) => {
        collection.classList.remove('loading');
      });
    }

    initializeSelection() {
      if (this.kind !== 'royal') return;
      const params = new URLSearchParams(window.location.search || '');
      const requested = params.get('model');
      const option = this.options.find((item) => normalize(item.dataset.royalModelSlug) === normalize(requested)) ||
        this.options.find((item) => normalize(item.dataset.royalModelSlug) === 'ocho-negro') ||
        this.options[0];
      if (option) this.selectRoyalModel(option, false);
    }

    selectRoyalModel(option, updateHistory) {
      const slug = option?.dataset.royalModelSlug || '';
      if (!slug) return;
      this.options.forEach((item) => {
        const selected = item === option;
        item.classList.toggle('is-selected', selected);
        item.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });

      if (updateHistory) {
        window.BrilintonDeviceContext?.rememberRoyalPop(slug, 'collection-device-navigation');
        const url = new URL(window.location.href);
        url.searchParams.delete('device');
        url.searchParams.set('model', slug);
        history.pushState({ royalPopModel: slug }, '', `${url.pathname}${url.search}${url.hash}`);
      }

      document.dispatchEvent(new CustomEvent('royal-pop:model-change', {
        detail: { modelSlug: slug, source: 'collection-device-navigation' }
      }));

      this.centerOption(option);
    }

    centerOption(option) {
      if (!this.viewport || !option || !this.root.classList.contains('has-overflow')) return;
      const target = option.offsetLeft - ((this.viewport.clientWidth - option.offsetWidth) / 2);
      this.viewport.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    }

    scroll(direction) {
      if (!this.viewport) return;
      const amount = Math.max(240, this.viewport.clientWidth * .78);
      this.viewport.scrollBy({ left: direction * amount, behavior: 'smooth' });
    }

    scheduleRefresh() {
      if (this.refreshFrame) return;
      this.refreshFrame = window.requestAnimationFrame(() => {
        this.refreshFrame = 0;
        this.refresh();
      });
    }

    scheduleArrowRefresh() {
      if (this.arrowFrame) return;
      this.arrowFrame = window.requestAnimationFrame(() => {
        this.arrowFrame = 0;
        this.refreshArrows();
      });
    }

    scheduleCompactRefresh() {
      if (this.compactFrame) return;
      this.compactFrame = window.requestAnimationFrame(() => {
        this.compactFrame = 0;
        this.refreshCompactState();
      });
    }

    refresh() {
      if (!this.viewport || !this.track) return;
      const overflow = this.track.scrollWidth > this.viewport.clientWidth + 2;
      this.root.classList.toggle('has-overflow', overflow);
      this.refreshArrows();
      this.refreshCompactState();
    }

    refreshArrows() {
      if (!this.viewport) return;
      const overflow = this.root.classList.contains('has-overflow');
      const max = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const left = this.viewport.scrollLeft;
      if (this.previous) this.previous.hidden = !overflow || left <= 2;
      if (this.next) this.next.hidden = !overflow || left >= max - 2;
    }

    refreshCompactState() {
      if (!this.sentinel) return;
      const threshold = headerOffset() + 1;
      const top = this.sentinel.getBoundingClientRect().top;
      let nextCompact = this.compact;

      if (!this.compact && top <= threshold + COMPACT_ENTER_BUFFER) nextCompact = true;
      if (this.compact && top > threshold + COMPACT_EXIT_BUFFER) nextCompact = false;

      if (nextCompact === this.compact) return;
      this.compact = nextCompact;
      this.root.classList.toggle('is-compact', nextCompact);
      this.scheduleArrowRefresh();
    }
  }

  const boot = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => new CollectionDeviceNavigation(root));
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target || document));
  window.addEventListener('brilinton:layout-refresh', () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      root.__collectionDeviceNavigation?.refresh();
    });
  });
})();
